<template>
  <div class="manage-ptm">
    <div class="ptm-card">
      <!-- 1. Executive Unified Header -->
      <header class="ptm-header">
        <div class="ptm-header__lead">
          <div class="ptm-header__icon-box">
            <feather-icon icon="MessageSquareIcon" size="22" />
          </div>
          <div class="ptm-header__meta">
            <div class="ptm-header__tags">
              <span class="ptm-eyebrow">{{ $t("Student Records") }}</span>
              <span class="ptm-stat-pill">
                <span class="ptm-stat-pill__dot"></span>
                <span class="ptm-stat-pill__count">{{ totalCount }}</span>
                <span class="ptm-stat-pill__label">
                  {{ totalCount === 1 ? $t("record") : $t("total records") }}
                </span>
              </span>
            </div>
            <h1 class="ptm-title">{{ $t("Parent–Teacher Meetings") }}</h1>
            <p class="ptm-subtitle">
              {{
                $t(
                  "Track student progress discussions, feedback, and action agreements"
                )
              }}
            </p>
          </div>
        </div>

        <div class="ptm-header__actions">
          <button type="button" class="ptm-btn-primary" @click="openAddModal">
            <span class="ptm-btn-primary__icon-wrap">
              <feather-icon icon="PlusIcon" size="15" />
            </span>
            <span>{{ $t("Add Summary") }}</span>
          </button>
        </div>
      </header>

      <!-- 2. Integrated Minimalist Toolbar (Filters & Search) -->
      <div class="ptm-toolbar">
        <!-- Left: Student Filter / Live Search -->
        <div class="ptm-toolbar__search" v-if="false">
          <!-- Selected Student Chip -->
          <div v-if="selectedStudent" class="ptm-chip-selected">
            <div class="ptm-chip-selected__avatar">
              <span>{{
                (selectedStudent.name || "S").charAt(0).toUpperCase()
              }}</span>
            </div>
            <div class="ptm-chip-selected__info">
              <span class="ptm-chip-selected__name">{{
                selectedStudent.name
              }}</span>
              <span v-if="selectedStudent.grNo" class="ptm-chip-selected__gr">
                GR: {{ selectedStudent.grNo }}
              </span>
              <span
                v-if="selectedStudent.className"
                class="ptm-chip-selected__class"
              >
                · {{ selectedStudent.className }}
              </span>
            </div>
            <button
              type="button"
              class="ptm-chip-selected__remove"
              :title="$t('Clear filter')"
              @click="clearFilterStudent"
            >
              <feather-icon icon="XIcon" size="13" />
            </button>
          </div>

          <!-- Search Input -->
          <div
            v-else
            class="ptm-search-input-wrap"
            :class="{
              'ptm-search-input-wrap--focused': showFilterStudentDropdown,
            }"
          >
            <feather-icon
              icon="SearchIcon"
              size="15"
              class="ptm-search-input-icon"
            />
            <b-form-input
              v-model="studentSearchQuery"
              class="ptm-search-field"
              :placeholder="$t('Search student by name or GR No...')"
              @input="onStudentSearchChange"
              @focus="studentSearchFocused = true"
              @blur="onStudentSearchBlur"
            />
            <button
              v-if="studentSearchQuery"
              type="button"
              class="ptm-search-clear-btn"
              @mousedown.prevent
              @click="clearStudentSearchQuery"
            >
              <feather-icon icon="XIcon" size="13" />
            </button>
            <b-spinner
              v-else-if="studentSearchLoading"
              small
              variant="primary"
              class="ptm-search-spinner"
            />

            <!-- Autocomplete Dropdown Options -->
            <transition name="ptm-dropdown-fade">
              <div v-if="showFilterStudentDropdown" class="ptm-search-dropdown">
                <div
                  v-if="studentSearchQuery.length < 2"
                  class="ptm-search-state ptm-search-state--hint"
                >
                  <feather-icon icon="InfoIcon" size="14" />
                  <span>{{ $t("Type at least 2 characters to search") }}</span>
                </div>

                <template v-else>
                  <div v-if="studentSearchLoading" class="ptm-search-state">
                    <b-spinner small variant="primary" />
                    <span>{{ $t("Searching students...") }}</span>
                  </div>

                  <div
                    v-else-if="filteredStudentOptions.length === 0"
                    class="ptm-search-state ptm-search-state--empty"
                  >
                    <div class="ptm-search-state__icon">
                      <feather-icon icon="UserXIcon" size="20" />
                    </div>
                    <strong>{{ $t("No students found") }}</strong>
                    <small>{{ $t("Try another name or GR number") }}</small>
                  </div>

                  <template v-else>
                    <div class="ptm-search-dropdown__header">
                      <span
                        >{{ filteredStudentOptions.length }}
                        {{ $t("students found") }}</span
                      >
                    </div>
                    <div class="ptm-student-options">
                      <button
                        v-for="student in filteredStudentOptions"
                        :key="student.id"
                        type="button"
                        class="ptm-student-option"
                        @mousedown.prevent
                        @click="selectFilterStudent(student)"
                      >
                        <div class="ptm-student-option__content">
                          <span class="ptm-student-option__name">{{
                            student.name
                          }}</span>
                          <span
                            v-if="student.grNo"
                            class="ptm-student-option__meta"
                          >
                            GR {{ student.grNo }}
                          </span>
                        </div>
                      </button>
                    </div>
                  </template>
                </template>
              </div>
            </transition>
          </div>
        </div>

        <!-- Right: Date filters & Reset -->
        <div class="ptm-toolbar__filters">
          <div class="ptm-date-field-wrap">
            <feather-icon
              icon="CalendarIcon"
              size="15"
              class="ptm-date-field-icon"
            />
            <flat-pickr
              v-model="filters.dateFrom"
              class="ptm-date-field"
              :config="dateConfig"
              :placeholder="$t('Date From')"
              @on-change="onDateFilterChange"
            />
          </div>

          <div class="ptm-date-field-wrap">
            <feather-icon
              icon="CalendarIcon"
              size="15"
              class="ptm-date-field-icon"
            />
            <flat-pickr
              v-model="filters.dateTo"
              class="ptm-date-field"
              :config="dateConfig"
              :placeholder="$t('Date To')"
              @on-change="onDateFilterChange"
            />
          </div>

          <!-- Reset All Filters Pill Button -->
          <transition name="ptm-dropdown-fade">
            <button
              v-if="hasActiveFilters"
              type="button"
              class="ptm-btn-reset"
              :title="$t('Reset all filters')"
              @click="clearFilters"
            >
              <feather-icon icon="RotateCcwIcon" size="13" />
              <span>{{ $t("Reset") }}</span>
              <span v-if="activeFiltersCount" class="ptm-btn-reset__badge">
                {{ activeFiltersCount }}
              </span>
            </button>
          </transition>

          <!-- Print Report Button -->
          <button
            type="button"
            class="ptm-btn-print"
            :title="$t('Print Report')"
            @click="openPrintView"
          >
            <feather-icon icon="PrinterIcon" size="15" />
            <span>{{ $t("Print") }}</span>
          </button>
        </div>
      </div>

      <!-- 3. Modern Table Ledger (Directly flows from toolbar) -->
      <div class="ptm-table-wrap" ref="tableWrapper">
        <b-table
          class="ptm-table mb-0"
          :items="tableRows"
          :fields="fields"
          :busy="listLoading"
          responsive
          show-empty
        >
          <!-- Loading State -->
          <template #table-busy>
            <div class="ptm-table-state ptm-table-state--busy">
              <div class="ptm-spinner-ring">
                <b-spinner variant="primary" />
              </div>
              <span class="ptm-table-state__title">{{
                $t("Loading meeting records...")
              }}</span>
              <small class="ptm-table-state__desc">{{
                $t("Please wait while we fetch the latest summaries.")
              }}</small>
            </div>
          </template>

          <!-- Empty State -->
          <template #empty>
            <div class="ptm-table-state ptm-table-state--empty">
              <div class="ptm-empty-icon-wrap">
                <feather-icon icon="InboxIcon" size="32" />
              </div>
              <h3 class="ptm-empty-title">{{ $t("No PTM records found") }}</h3>
              <p class="ptm-empty-desc">
                {{
                  hasActiveFilters
                    ? $t(
                        "No meeting records match your active filters. Try clearing your search query or date range."
                      )
                    : $t(
                        "There are no parent-teacher meeting summaries recorded yet."
                      )
                }}
              </p>
              <div class="ptm-empty-actions">
                <button
                  v-if="hasActiveFilters"
                  type="button"
                  class="ptm-btn-ghost-sm"
                  @click="clearFilters"
                >
                  <feather-icon icon="RotateCcwIcon" size="13" class="mr-25" />
                  {{ $t("Clear Filters") }}
                </button>
                <button
                  type="button"
                  class="ptm-btn-primary-sm"
                  @click="openAddModal"
                >
                  <feather-icon icon="PlusIcon" size="13" class="mr-25" />
                  {{ $t("Add First Summary") }}
                </button>
              </div>
            </div>
          </template>

          <!-- Cell: Row Number # -->
          <template #cell(sno)="data">
            <span class="ptm-cell-index">
              {{ String(data.value).padStart(2, "0") }}
            </span>
          </template>

          <!-- Cell: Student -->
          <template #cell(name)="data">
            <div class="ptm-student-cell">
              <div class="ptm-student-cell__info">
                <span class="ptm-student-cell__name">{{ data.value }}</span>
                <span
                  v-if="data.item.grNo && data.item.grNo !== '—'"
                  class="ptm-student-cell__sub"
                >
                  GR {{ data.item.grNo }}
                </span>
              </div>
            </div>
          </template>

          <!-- Cell: Father Name -->
          <template #cell(father_name)="data">
            <span class="ptm-plain-cell">{{ data.value }}</span>
          </template>

          <!-- Cell: Class - Section -->
          <template #cell(class)="data">
            <span class="ptm-plain-cell"
              >{{ data.item.className }} - {{ data.item.secName }}</span
            >
          </template>

          <!-- Cell: Date -->
          <template #cell(date)="data">
            <span class="ptm-date-pill-cell">
              <feather-icon
                icon="CalendarIcon"
                size="12"
                class="ptm-date-pill-cell__icon"
              />
              <span>{{ formatTableDate(data.value) }}</span>
            </span>
          </template>

          <!-- Cell: Summary -->
          <template #cell(summary)="data">
            <div class="ptm-summary-card">
              <div class="ptm-summary-card__bar" />
              <p class="ptm-summary-card__text">{{ data.value || "—" }}</p>
            </div>
          </template>
        </b-table>
      </div>

      <!-- 4. Integrated High-End Footer: Pagination & Per Page -->
      <footer class="ptm-footer">
        <div class="ptm-footer__left">
          <div class="ptm-footer__page-size">
            <span class="ptm-footer__label">{{ $t("Rows per page") }}:</span>
            <v-select
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="ptm-per-page-select"
              @input="onPageSizeChange"
            />
          </div>
        </div>

        <div class="ptm-footer__center">
          <div class="ptm-footer__range">
            {{ $t("Showing") }}
            <span class="ptm-footer__num">{{ displayedRange.start }}</span>
            –
            <span class="ptm-footer__num">{{ displayedRange.end }}</span>
            {{ $t("of") }}
            <span class="ptm-footer__num">{{ totalCount }}</span>
            {{ $t("records") }}
          </div>
        </div>

        <div class="ptm-footer__right">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            align="center"
            class="ptm-pagination mb-0"
            hide-goto-end-buttons
            @change="onPageChange"
          >
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="16" />
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="16" />
            </template>
          </b-pagination>
        </div>
      </footer>
    </div>

    <!-- Add Summary Modal -->
    <b-modal
      id="ptm-add-modal"
      v-model="addModalVisible"
      hide-footer
      centered
      size="lg"
      modal-class="ptm-modal-wrapper"
      content-class="ptm-modal-content"
      body-class="ptm-modal-body"
      header-class="ptm-modal-header"
      @hidden="resetAddForm"
    >
      <template #modal-title>
        <div class="ptm-modal-title">
          <span class="ptm-modal-title-icon">
            <feather-icon icon="PlusIcon" size="18" />
          </span>
  <div>
            <h5 class="mb-0">{{ $t("Add PTM Summary") }}</h5>
            <small class="ptm-modal-subtitle">
              {{
                $t(
                  "Record discussion points, feedback, and student observations"
                )
              }}
            </small>
          </div>
        </div>
      </template>

      <b-form class="ptm-modal-form" @submit.prevent="savePtm">
        <div class="ptm-modal-form-panel">
          <!-- Student -->
          <div class="ptm-modal-field">
            <label class="ptm-modal-field-label" for="ptm-modal-student-search">
              {{ $t("Student") }}
            </label>

            <div v-if="modalSelectedStudent" class="ptm-modal-selected-student">
              <div class="ptm-modal-selected-student__info">
                <span class="ptm-modal-selected-student__name">{{
                  modalSelectedStudent.name
                }}</span>
                <span
                  v-if="modalSelectedStudent.grNo"
                  class="ptm-modal-selected-student__gr"
                >
                  GR {{ modalSelectedStudent.grNo }}
                </span>
              </div>
              <button
                type="button"
                class="ptm-modal-selected-student__remove"
                :title="$t('Change student')"
                @click="clearModalStudent"
              >
                <feather-icon icon="XIcon" size="14" />
              </button>
            </div>

            <div
              v-else
              class="ptm-modal-search"
              :class="{ 'ptm-modal-search--active': showModalStudentDropdown }"
            >
              <feather-icon
                icon="SearchIcon"
                size="16"
                class="ptm-modal-search-icon"
              />
              <b-form-input
                id="ptm-modal-student-search"
                v-model="modalStudentSearchQuery"
                class="ptm-modal-input ptm-modal-search-input"
                :placeholder="$t('Search by name or GR No...')"
                @input="onModalStudentSearchChange"
                @focus="modalStudentSearchFocused = true"
                @blur="onModalStudentSearchBlur"
              />
              <button
                v-if="modalStudentSearchQuery"
                type="button"
                class="ptm-modal-search-clear"
                @mousedown.prevent
                @click="clearModalStudentSearchQuery"
              >
                <feather-icon icon="XIcon" size="13" />
              </button>
              <b-spinner
                v-else-if="modalStudentSearchLoading"
                small
                variant="primary"
                class="ptm-modal-search-spinner"
              />

              <transition name="ptm-dropdown-fade">
                <div
                  v-if="showModalStudentDropdown"
                  class="ptm-search-dropdown ptm-search-dropdown--modal"
                >
                  <div
                    v-if="modalStudentSearchQuery.length < 2"
                    class="ptm-search-state ptm-search-state--hint"
                  >
                    <feather-icon icon="InfoIcon" size="14" />
                    <span>{{
                      $t("Type at least 2 characters to search")
                    }}</span>
                  </div>

                  <template v-else>
                    <div
                      v-if="modalStudentSearchLoading"
                      class="ptm-search-state"
                    >
                      <b-spinner small variant="primary" />
                      <span>{{ $t("Searching students...") }}</span>
                    </div>

                    <div
                      v-else-if="modalFilteredStudentOptions.length === 0"
                      class="ptm-search-state ptm-search-state--empty"
                    >
                      <div class="ptm-search-state__icon">
                        <feather-icon icon="UserXIcon" size="20" />
                      </div>
                      <strong>{{ $t("No students found") }}</strong>
                      <small>{{ $t("Try another name or GR number") }}</small>
                    </div>

                    <template v-else>
                      <div class="ptm-search-dropdown__header">
                        <span
                          >{{ modalFilteredStudentOptions.length }}
                          {{ $t("students found") }}</span
                        >
                      </div>
                      <div class="ptm-student-options">
                        <button
                          v-for="student in modalFilteredStudentOptions"
                          :key="'modal-' + student.id"
                          type="button"
                          class="ptm-student-option"
                          @mousedown.prevent
                          @click="selectModalStudent(student)"
                        >
                          <div class="ptm-student-option__content">
                            <span class="ptm-student-option__name">{{
                              student.name
                            }}</span>
                            <span
                              v-if="student.grNo"
                              class="ptm-student-option__meta"
                            >
                              GR {{ student.grNo }}
                            </span>
                          </div>
                        </button>
                      </div>
                    </template>
                  </template>
                </div>
              </transition>
            </div>

            <small v-if="addFormErrors.student" class="ptm-modal-error">
              {{ addFormErrors.student }}
            </small>
          </div>

          <!-- Meeting Date -->
          <div class="ptm-modal-field">
            <label class="ptm-modal-field-label" for="ptm-form-date">
              {{ $t("Meeting Date") }}
            </label>
            <div class="ptm-modal-search ptm-modal-search--date">
              <feather-icon
                icon="CalendarIcon"
                size="16"
                class="ptm-modal-search-icon"
              />
              <flat-pickr
                id="ptm-form-date"
                v-model="ptmForm.date"
                class="ptm-modal-input ptm-modal-search-input ptm-modal-date-input"
                :config="dateConfig"
                :placeholder="$t('Pick meeting date')"
              />
            </div>
            <small v-if="addFormErrors.date" class="ptm-modal-error">
              {{ addFormErrors.date }}
            </small>
          </div>

          <!-- Meeting Summary -->
          <div class="ptm-modal-field ptm-modal-field--last">
            <label class="ptm-modal-field-label" for="ptm-form-summary">
              {{ $t("Meeting Summary & Discussion Points") }}
            </label>
            <b-form-textarea
              id="ptm-form-summary"
              v-model="ptmForm.summary"
              class="ptm-modal-input ptm-modal-textarea"
              rows="5"
              :placeholder="$t('Enter summary and discussion points...')"
            />
            <small v-if="addFormErrors.summary" class="ptm-modal-error">
              {{ addFormErrors.summary }}
            </small>
          </div>
        </div>

        <div class="ptm-modal-actions">
          <button
            type="button"
            class="ptm-modal-cancel-btn"
            :disabled="savingPtm"
            @click="addModalVisible = false"
          >
            {{ $t("Cancel") }}
          </button>
          <button
            type="submit"
            class="ptm-modal-save-btn"
            :disabled="savingPtm"
          >
            <b-spinner v-if="savingPtm" small type="grow" class="mr-50" />
            <template v-else>
              <feather-icon icon="CheckIcon" size="16" class="mr-50" />
            </template>
            <span>{{ savingPtm ? $t("Saving...") : $t("Save Summary") }}</span>
          </button>
        </div>
      </b-form>
    </b-modal>

    <!-- ─── PRINT VIEW OVERLAY ──────────────────────────────────────────── -->
    <div v-if="showPrintView" class="ptm-print-view">

      <!-- Controls (hidden in actual print) -->
      <div class="ptm-print-controls no-print">
        <button type="button" class="ptm-print-back-btn" @click="closePrintView">
          <feather-icon icon="ArrowLeftIcon" size="16" />
          <span>{{ $t("Back") }}</span>
        </button>
        <div class="ptm-print-controls__right">
          <span v-if="!printLoading && !printError" class="ptm-print-count-label">
            {{ printRows.length }} {{ $t("records") }}
          </span>
          <button
            type="button"
            class="ptm-print-now-btn"
            :disabled="printLoading || !!printError"
            @click="doPrint"
          >
            <feather-icon icon="PrinterIcon" size="15" />
            <span>{{ $t("Print Now") }}</span>
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="printLoading" class="ptm-print-loading">
        <b-spinner variant="primary" style="width:3rem;height:3rem;" />
        <p class="ptm-print-loading__text">{{ $t("Loading all records for print...") }}</p>
      </div>

      <!-- Error State -->
      <div v-else-if="printError" class="ptm-print-error">
        <feather-icon icon="AlertTriangleIcon" size="36" class="text-danger mb-1" />
        <p>{{ printError }}</p>
        <button type="button" class="ptm-print-now-btn" @click="openPrintView">
          {{ $t("Retry") }}
        </button>
      </div>

      <!-- Printable Content -->
      <div v-else class="ptm-print-document">
        <!-- Report Header -->
        <div class="ptm-print-doc-header">
          <div class="ptm-print-doc-header__left">
            <h1 class="ptm-print-doc-title">{{ $t("Parent–Teacher Meeting Report") }}</h1>
            <p class="ptm-print-doc-subtitle">
              <span v-if="filters.dateFrom || filters.dateTo">
                <feather-icon icon="CalendarIcon" size="13" class="mr-25" />
                <span v-if="filters.dateFrom">{{ $t("From") }}: <strong>{{ formatTableDate(filters.dateFrom) }}</strong></span>
                <span v-if="filters.dateFrom && filters.dateTo"> &nbsp;–&nbsp; </span>
                <span v-if="filters.dateTo">{{ $t("To") }}: <strong>{{ formatTableDate(filters.dateTo) }}</strong></span>
              </span>
              <span v-else>{{ $t("All Records") }}</span>
            </p>
          </div>
          <div class="ptm-print-doc-header__right">
            <span class="ptm-print-doc-date">
              {{ $t("Generated") }}: {{ printGeneratedDate }}
            </span>
            <span class="ptm-print-doc-total">
              {{ $t("Total Records") }}: <strong>{{ printRows.length }}</strong>
            </span>
          </div>
        </div>

        <!-- Table -->
        <table class="ptm-print-table">
          <thead>
            <tr>
              <th class="ptm-print-th--idx">#</th>
              <th>{{ $t("Student") }}</th>
              <th>{{ $t("Father Name") }}</th>
              <th>{{ $t("Class") }}</th>
              <th>{{ $t("Date") }}</th>
              <th class="ptm-print-th--summary">{{ $t("Summary") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in printRows" :key="row.id || row.sno" class="ptm-print-tr">
              <td class="ptm-print-td--idx">{{ row.sno }}</td>
              <td>
                <div class="ptm-print-student">
                  <span class="ptm-print-student__name">{{ row.name }}</span>
                  <span v-if="row.grNo && row.grNo !== '—'" class="ptm-print-student__gr">GR {{ row.grNo }}</span>
                </div>
              </td>
              <td>{{ row.father_name }}</td>
              <td class="ptm-print-td--class">{{ row.className }} - {{ row.secName }}</td>
              <td class="ptm-print-td--date">{{ formatTableDate(row.date) }}</td>
              <td class="ptm-print-td--summary">{{ row.summary || "—" }}</td>
            </tr>
            <tr v-if="!printRows.length">
              <td colspan="6" class="ptm-print-empty">{{ $t("No records found.") }}</td>
            </tr>
          </tbody>
        </table>

        <!-- Print Footer -->
        <div class="ptm-print-doc-footer">
          {{ $t("Total") }}: {{ printRows.length }} {{ $t("record(s)") }}
        </div>
      </div>
    </div>
    <!-- ─────────────────────────────────────────────────────────────────── -->

  </div>
</template>

<script>
import {
  BForm,
  BFormInput,
  BFormGroup,
  BFormTextarea,
  BTable,
  BPagination,
  BModal,
  BSpinner,
  VBTooltip,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { mapActions } from "vuex";

export default {
  name: "ManagePtm",
  components: {
    BForm,
    BFormInput,
    BFormGroup,
    BFormTextarea,
    BTable,
    BPagination,
    BModal,
    BSpinner,
    vSelect,
    flatPickr,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      rights: {},
      rows: [],
      totalCount: 0,
      currentPage: 1,
      perPage: 10,
      listLoading: false,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "25", value: 25 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
      filters: {
        sID: null,
        dateFrom: "",
        dateTo: "",
      },
      dateConfig: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
        allowInput: true,
        disableMobile: true,
        onReady(_selectedDates, _dateStr, instance) {
          instance.calendarContainer.classList.add("ptm-flatpickr-popup");
        },
      },
      fields: [
        { key: "sno", label: "#" },
        { key: "name", label: "Student" },
        { key: "father_name", label: "Father Name" },
        { key: "class", label: "Class" },
        { key: "date", label: "Date" },
        { key: "summary", label: "Summary" },
      ],
      selectedStudent: null,
      studentSearchQuery: "",
      studentSearchFocused: false,
      filteredStudentOptions: [],
      studentSearchLoading: false,
      studentSearchCache: {},
      studentSearchTimer: null,
      lastStudentSearchToken: 0,
      addModalVisible: false,
      savingPtm: false,
      ptmForm: {
        date: "",
        summary: "",
      },
      addFormErrors: {
        student: "",
        date: "",
        summary: "",
      },
      modalSelectedStudent: null,
      modalStudentSearchQuery: "",
      modalStudentSearchFocused: false,
      modalFilteredStudentOptions: [],
      modalStudentSearchLoading: false,
      modalStudentSearchTimer: null,
      modalLastStudentSearchToken: 0,
      // ── Print View ────────────────────────────────────────────────────
      showPrintView: false,
      printLoading: false,
      printRows: [],
      printError: null,
      printGeneratedDate: "",
    };
  },
  computed: {
    hasActiveFilters() {
      return !!(
        this.filters.sID ||
        this.filters.dateFrom ||
        this.filters.dateTo ||
        this.selectedStudent
      );
    },
    activeFiltersCount() {
      let count = 0;
      if (this.selectedStudent || this.filters.sID) count++;
      if (this.filters.dateFrom) count++;
      if (this.filters.dateTo) count++;
      return count;
    },
    displayedRange() {
      if (!this.totalCount) return { start: 0, end: 0 };
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
    showFilterStudentDropdown() {
      return (
        this.studentSearchFocused &&
        !this.selectedStudent &&
        this.studentSearchQuery.length > 0
      );
    },
    showModalStudentDropdown() {
      return (
        this.modalStudentSearchFocused &&
        !this.modalSelectedStudent &&
        this.modalStudentSearchQuery.length > 0
      );
    },
    tableRows() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.rows.map((row, index) => ({
        ...row,
        sno: start + index + 1,
      }));
    },
  },
  created() {
    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({
        name: "misc-not-authorized",
      });
    } else {
      this.rights = this.$store.state.rights[this.$route.name];
      this.reload(true);
    }
  },
  beforeDestroy() {
    if (this.studentSearchTimer) clearTimeout(this.studentSearchTimer);
    if (this.modalStudentSearchTimer)
      clearTimeout(this.modalStudentSearchTimer);
  },
  methods: {
    ...mapActions({ get: "get", post: "post" }),

    sliceDate(val) {
      if (!val) return "";
      if (val instanceof Date && !Number.isNaN(val.getTime())) {
        const y = val.getFullYear();
        const m = String(val.getMonth() + 1).padStart(2, "0");
        const d = String(val.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
      }
      return String(val).slice(0, 10);
    },

    displayCell(value) {
      if (value === null || value === undefined) return "—";
      const text = String(value).trim();
      return text || "—";
    },

    formatTableDate(dateStr) {
      if (!dateStr) return "—";
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) return "—";
      return date.toLocaleDateString("en-UK", {
        year: "numeric",
        day: "numeric",
        month: "short",
      });
    },

    buildPtmQuery() {
      const params = new URLSearchParams({
        db: this.$store.state.userData.db,
        campusID: String(this.$store.state.userData.cId),
        page: String(this.currentPage),
        pageSize: String(this.perPage),
      });

      if (this.filters.sID) {
        params.append("sID", String(this.filters.sID));
      }
      if (this.filters.dateFrom) {
        params.append("dateFrom", this.sliceDate(this.filters.dateFrom));
      }
      if (this.filters.dateTo) {
        params.append("dateTo", this.sliceDate(this.filters.dateTo));
      }

      return params.toString();
    },

    extractPtmList(response) {
      if (!response) return { items: [], total: 0, hasExplicitTotal: false };

      // 1. Identify rows array from various standard backend response shapes
      let list = [];
      if (Array.isArray(response)) {
        list = response;
      } else if (response.data && Array.isArray(response.data)) {
        list = response.data;
      } else if (
        response.data &&
        response.data.data &&
        Array.isArray(response.data.data)
      ) {
        list = response.data.data;
      } else if (
        response.data &&
        response.data.items &&
        Array.isArray(response.data.items)
      ) {
        list = response.data.items;
      } else if (
        response.data &&
        response.data.records &&
        Array.isArray(response.data.records)
      ) {
        list = response.data.records;
      } else if (
        response.data &&
        response.data.list &&
        Array.isArray(response.data.list)
      ) {
        list = response.data.list;
      } else if (response.items && Array.isArray(response.items)) {
        list = response.items;
      } else if (response.records && Array.isArray(response.records)) {
        list = response.records;
      } else if (response.list && Array.isArray(response.list)) {
        list = response.list;
      }

      // 2. Extract explicit total count from response metadata
      let explicitTotal = null;
      const candidates = [
        response.totalRecords,
        response.totalCount,
        response.total,
        response.count,
        response.totalRows,
        response.recordsTotal,
        response.data && response.data.totalRecords,
        response.data && response.data.totalCount,
        response.data && response.data.total,
        response.data && response.data.count,
        response.data && response.data.totalRows,
        response.data && response.data.recordsTotal,
        response.pagination && response.pagination.totalRecords,
        response.pagination && response.pagination.totalCount,
        response.pagination && response.pagination.total,
      ];

      for (const val of candidates) {
        if (val !== undefined && val !== null && !Number.isNaN(Number(val))) {
          explicitTotal = Number(val);
          break;
        }
      }

      const hasExplicitTotal = explicitTotal !== null;
      const total = hasExplicitTotal ? explicitTotal : list.length;

      return {
        items: list.map((item) => this.mapPtmRow(item)),
        total: Number(total) || 0,
        hasExplicitTotal,
      };
    },

    mapPtmRow(item) {
      return {
        id: item.id,
        sID: item.sID,
        name: this.displayCell(item.name),
        studentName: this.displayCell(item.name),
        grNo: this.displayCell(item.grNo),
        father_name: this.displayCell(item.father_name),
        className: this.displayCell(item.className),
        secName: this.displayCell(item.secName),
        date: item.date || "",
        summary: item.summary || "",
      };
    },

    async reload(resetPage) {
      if (resetPage) this.currentPage = 1;
      this.listLoading = true;
      try {
        const query = this.buildPtmQuery();
        const response = await this.get({
          url: `${this.$store.state.domain}ParentTeachersMeeting/GetByPagination?${query}`,
          token: this.$store.state.userData.token,
          dashboard: true,
        });
        const { items, total, hasExplicitTotal } =
          this.extractPtmList(response);
        this.rows = items;

        if (hasExplicitTotal) {
          this.totalCount = total;
        } else {
          // If the backend API did not provide an explicit totalCount property in the response:
          if (items.length < this.perPage && this.currentPage === 1) {
            this.totalCount = items.length;
          } else if (resetPage || !this.totalCount) {
            // Retrieve exact total count across all pages via pageSize=1000
            this.fetchTotalCount();
          }
        }
      } catch (e) {
        console.error("Failed to load PTM list:", e);
        this.rows = [];
        this.totalCount = 0;
        if (this.$bvToast) {
          this.$bvToast.toast(this.$t("Failed to load PTM records."), {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          });
        }
      } finally {
        this.listLoading = false;
      }
    },

    async fetchTotalCount() {
      try {
        const params = new URLSearchParams({
          db: this.$store.state.userData.db,
          campusID: String(this.$store.state.userData.cId),
          page: "1",
          pageSize: "1000",
        });

        if (this.filters.sID) {
          params.append("sID", String(this.filters.sID));
        }
        if (this.filters.dateFrom) {
          params.append("dateFrom", this.sliceDate(this.filters.dateFrom));
        }
        if (this.filters.dateTo) {
          params.append("dateTo", this.sliceDate(this.filters.dateTo));
        }

        const res = await this.get({
          url: `${this.$store.state.domain}ParentTeachersMeeting/GetByPagination?${params.toString()}`,
          token: this.$store.state.userData.token,
          dashboard: true,
        });
        const { items, total, hasExplicitTotal } = this.extractPtmList(res);
        this.totalCount = hasExplicitTotal ? total : items.length;
      } catch (err) {
        console.warn("Could not fetch total count:", err);
      }
    },

    onPageSizeChange(val) {
      if (val) this.perPage = Number(val);
      this.currentPage = 1;
      this.reload(true);
    },

    onPageChange(page) {
      if (page && page !== this.currentPage) {
        this.currentPage = page;
      }
      this.$nextTick(() => {
        this.reload(false);
      });
    },

    onDateFilterChange() {
      this.reload(true);
    },

    clearDates() {
      this.filters.dateFrom = "";
      this.filters.dateTo = "";
      this.reload(true);
    },

    onStudentSearchChange() {
      if (this.studentSearchTimer) clearTimeout(this.studentSearchTimer);
      this.studentSearchTimer = setTimeout(() => {
        this.searchStudents("filter");
      }, 700);
    },

    onModalStudentSearchChange() {
      if (this.modalStudentSearchTimer)
        clearTimeout(this.modalStudentSearchTimer);
      this.modalStudentSearchTimer = setTimeout(() => {
        this.searchStudents("modal");
      }, 700);
    },

    onStudentSearchBlur() {
      setTimeout(() => {
        this.studentSearchFocused = false;
      }, 180);
    },

    clearStudentSearchQuery() {
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
    },

    onModalStudentSearchBlur() {
      setTimeout(() => {
        this.modalStudentSearchFocused = false;
      }, 180);
    },

    clearModalStudentSearchQuery() {
      this.modalStudentSearchQuery = "";
      this.modalFilteredStudentOptions = [];
    },

    mapStudentSearchOption(student) {
      let className = "";
      let secName = "";
      if (student.meta_class) {
        const parts = String(student.meta_class).split(" - ");
        className = (parts[0] || "").trim();
        secName = (parts[1] || "").trim();
      } else {
        className = student.className || student.cls || "";
        secName =
          student.secName || student.sectionName || student.section || "";
      }
      return {
        ...student,
        id: student.id || student.sID,
        name: student.name || "Unknown Student",
        grNo: student.grNo || student.gr || "",
        father_name: student.father_name || student.fatherName || "",
        className,
        secName,
        sectionName: secName,
      };
    },

    async searchStudents(context = "filter") {
      const isModal = context === "modal";
      const query = isModal
        ? this.modalStudentSearchQuery
        : this.studentSearchQuery;
      const term = (query || "").trim().toLowerCase();

      if (term.length < 2) {
        if (isModal) this.modalFilteredStudentOptions = [];
        else this.filteredStudentOptions = [];
        return;
      }

      if (!isModal && this.studentSearchCache[term]) {
        this.filteredStudentOptions = this.studentSearchCache[term];
        return;
      }

      const token = isModal
        ? ++this.modalLastStudentSearchToken
        : ++this.lastStudentSearchToken;

      if (isModal) this.modalStudentSearchLoading = true;
      else this.studentSearchLoading = true;

      try {
        const url =
          this.$store.state.domain +
          "Students/Search?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&keyword=" +
          encodeURIComponent(query) +
          "&type=";

        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
        });

        const activeToken = isModal
          ? this.modalLastStudentSearchToken
          : this.lastStudentSearchToken;
        if (token !== activeToken) return;

        let studentsData = [];
        if (response && Array.isArray(response.data)) {
          studentsData = response.data;
        } else if (Array.isArray(response)) {
          studentsData = response;
        }

        const mapped = studentsData.map((s) => this.mapStudentSearchOption(s));
        if (isModal) {
          this.modalFilteredStudentOptions = mapped;
        } else {
          this.filteredStudentOptions = mapped;
          this.studentSearchCache[term] = mapped;
        }
      } catch (e) {
        console.error("Student search error:", e);
        const activeToken = isModal
          ? this.modalLastStudentSearchToken
          : this.lastStudentSearchToken;
        if (token === activeToken) {
          if (isModal) this.modalFilteredStudentOptions = [];
          else this.filteredStudentOptions = [];
        }
      } finally {
        const activeToken = isModal
          ? this.modalLastStudentSearchToken
          : this.lastStudentSearchToken;
        if (token === activeToken) {
          if (isModal) this.modalStudentSearchLoading = false;
          else this.studentSearchLoading = false;
        }
      }
    },

    selectFilterStudent(student) {
      this.selectedStudent = { ...student };
      this.filters.sID = student.id || student.sID || null;
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
      this.studentSearchFocused = false;
      this.reload(true);
    },

    clearFilterStudent() {
      this.selectedStudent = null;
      this.filters.sID = null;
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
      this.studentSearchFocused = false;
      this.reload(true);
    },

    clearFilters() {
      this.filters = {
        sID: null,
        dateFrom: "",
        dateTo: "",
      };
      this.selectedStudent = null;
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
      this.studentSearchCache = {};
      this.reload(true);
    },

    onDateFilterChange() {
      this.reload(true);
    },

    openAddModal() {
      this.resetAddForm();
      this.addModalVisible = true;
    },

    resetAddForm() {
      this.ptmForm = { date: "", summary: "" };
      this.addFormErrors = { student: "", date: "", summary: "" };
      this.modalSelectedStudent = null;
      this.modalStudentSearchQuery = "";
      this.modalFilteredStudentOptions = [];
      this.modalStudentSearchFocused = false;
      this.savingPtm = false;
    },

    selectModalStudent(student) {
      this.modalSelectedStudent = { ...student };
      this.modalStudentSearchQuery = "";
      this.modalFilteredStudentOptions = [];
      this.modalStudentSearchFocused = false;
      this.addFormErrors.student = "";
    },

    clearModalStudent() {
      this.modalSelectedStudent = null;
      this.modalStudentSearchQuery = "";
      this.modalFilteredStudentOptions = [];
    },

    validateAddForm() {
      this.addFormErrors = { student: "", date: "", summary: "" };
      let valid = true;

      if (!this.modalSelectedStudent) {
        this.addFormErrors.student = this.$t("Student is required.");
        valid = false;
      }
      if (!this.ptmForm.date) {
        this.addFormErrors.date = this.$t("Date is required");
        valid = false;
      }
      if (!(this.ptmForm.summary || "").trim()) {
        this.addFormErrors.summary = this.$t("Summary is required.");
        valid = false;
      }

      return valid;
    },

    async savePtm() {
      if (!this.validateAddForm()) return;

      this.savingPtm = true;
      try {
        const body = {
          id: 0,
          campusID: this.$store.state.userData.cId,
          sID: this.modalSelectedStudent.id || this.modalSelectedStudent.sID,
          date: this.sliceDate(this.ptmForm.date),
          summary: (this.ptmForm.summary || "").trim(),
        };

        const status = await this.post({
          url:
            this.$store.state.domain +
            "ParentTeachersMeeting?db=" +
            this.$store.state.userData.db,
          body,
          message: "Summary added successfully.",
          context: this,
          token: this.$store.state.userData.token,
        });

        if (status) {
          this.addModalVisible = false;
          this.reload(true);
        }
      } catch (e) {
        console.error("Failed to save PTM:", e);
      } finally {
        this.savingPtm = false;
      }
    },

    async openPrintView() {
      this.showPrintView = true;
      this.printLoading = true;
      this.printError = null;
      this.printRows = [];
      this.printGeneratedDate = new Date().toLocaleDateString("en-UK", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });

      try {
        const params = new URLSearchParams({
          db: this.$store.state.userData.db,
          campusID: String(this.$store.state.userData.cId),
          page: "1",
          pageSize: "1000",
        });

        if (this.filters.sID) {
          params.append("sID", String(this.filters.sID));
        }
        if (this.filters.dateFrom) {
          params.append("dateFrom", this.sliceDate(this.filters.dateFrom));
        }
        if (this.filters.dateTo) {
          params.append("dateTo", this.sliceDate(this.filters.dateTo));
        }

        const response = await this.get({
          url: `${this.$store.state.domain}ParentTeachersMeeting/GetByPagination?${params.toString()}`,
          token: this.$store.state.userData.token,
          dashboard: true,
        });

        const { items } = this.extractPtmList(response);
        this.printRows = items.map((row, i) => ({
          ...row,
          sno: i + 1,
        }));
      } catch (e) {
        console.error("Failed to load PTM print data:", e);
        this.printError =
          "Failed to load records for printing. Please try again.";
      } finally {
        this.printLoading = false;
      }
    },

    closePrintView() {
      this.showPrintView = false;
      this.printRows = [];
      this.printError = null;
    },

    doPrint() {
      window.print();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

// Clean Luxury Theme Variables
.manage-ptm {
  --ptm-primary: #5a4ff5;
  --ptm-primary-dark: #4338ca;
  --ptm-primary-light: #f0eeff;
  --ptm-primary-glow: rgba(90, 79, 245, 0.25);
  --ptm-accent: #7c3aed;
  --ptm-accent-fuchsia: #d946ef;
  --ptm-text-dark: #0f172a;
  --ptm-text-main: #334155;
  --ptm-text-muted: #64748b;
  --ptm-text-light: #94a3b8;
  --ptm-border: #e2e8f0;
  --ptm-border-subtle: #f1f5f9;
  --ptm-bg-subtle: #f8fafc;
  --ptm-surface: #ffffff;
  --ptm-card-radius: 20px;
  --ptm-card-shadow: 0 20px 50px -15px rgba(27, 20, 75, 0.08),
    0 2px 10px rgba(27, 20, 75, 0.02);

  padding: 0.25rem 0 1.5rem;
  color: var(--ptm-text-main);
  font-family: inherit;
}

// 1. One-Piece Card Canvas
.ptm-card {
  position: relative;
  background: var(--ptm-surface);
  border: 1px solid rgba(226, 232, 240, 0.9);
  border-radius: var(--ptm-card-radius);
  box-shadow: var(--ptm-card-shadow);
  overflow: visible;
  transition: all 0.2s ease;
}

// 2. Executive Header Tier
.ptm-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  padding: 1.45rem 1.85rem;
  background: radial-gradient(
      ellipse 65% 75% at 0% 0%,
      rgba(124, 58, 237, 0.08),
      transparent 60%
    ),
    radial-gradient(
      ellipse 55% 55% at 100% 0%,
      rgba(90, 79, 245, 0.06),
      transparent 50%
    ),
    linear-gradient(180deg, #ffffff 0%, #faf9fe 100%);
  border-top-left-radius: var(--ptm-card-radius);
  border-top-right-radius: var(--ptm-card-radius);
  border-bottom: 1px solid var(--ptm-border-subtle);

  &__lead {
    display: flex;
    align-items: center;
    gap: 1.15rem;
  }

  &__icon-box {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border-radius: 16px;
    background: linear-gradient(135deg, #5a4ff5 0%, #7c3aed 100%);
    color: #ffffff;
    box-shadow: 0 10px 24px -4px var(--ptm-primary-glow),
      inset 0 1px 1px rgba(255, 255, 255, 0.4);
    flex-shrink: 0;
    transition: transform 0.2s ease;

    &:hover {
      transform: scale(1.04) rotate(-2deg);
    }
  }

  &__meta {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }

  &__tags {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    margin-bottom: 0.25rem;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-shrink: 0;
  }
}

.ptm-eyebrow {
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--ptm-accent);
}

// Live Status Pulse Pill
@keyframes ptm-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.55);
  }
  70% {
    box-shadow: 0 0 0 7px rgba(16, 185, 129, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}

@keyframes ptm-plus-pulse {
  0%,
  100% {
    transform: scale(1);
    background: rgba(255, 255, 255, 0.22);
  }
  50% {
    transform: scale(1.1);
    background: rgba(255, 255, 255, 0.32);
  }
}

.ptm-stat-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.18rem 0.65rem;
  border-radius: 999px;
  background: var(--ptm-primary-light);
  border: 1px solid rgba(90, 79, 245, 0.2);
  font-size: 0.74rem;
  font-weight: 700;
  color: var(--ptm-primary-dark);

  &__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #10b981;
    animation: ptm-pulse 2s infinite;
  }

  &__count {
    font-weight: 800;
    color: var(--ptm-primary-dark);
  }

  &__label {
    text-transform: capitalize;
    color: var(--ptm-text-muted);
  }
}

.ptm-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.2;
  color: var(--ptm-text-dark);
}

.ptm-subtitle {
  margin: 0.25rem 0 0;
  font-size: 0.86rem;
  line-height: 1.4;
  color: var(--ptm-text-muted);
}

// 3. Primary CTA Button
.ptm-btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  padding: 0.65rem 1.35rem;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #5a4ff5 0%, #7c3aed 100%);
  color: #ffffff;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 22px var(--ptm-primary-glow);
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);

  &__icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.22);
    animation: ptm-plus-pulse 2.4s ease-in-out infinite;

    ::v-deep svg {
      transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1);
    }
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(90, 79, 245, 0.36);
    color: #ffffff;

    .ptm-btn-primary__icon-wrap {
      animation: none;
      background: rgba(255, 255, 255, 0.3);

      ::v-deep svg {
        transform: rotate(90deg) scale(1.12);
      }
    }
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
  }
}

// Print Report Button
.ptm-btn-print {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 40px;
  padding: 0 1.15rem;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  color: #334155;
  font-size: 0.86rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05);
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: var(--ptm-primary);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(90, 79, 245, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
}

// 4. Integrated Control Toolbar
.ptm-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.85rem;
  padding: 0.85rem 1.85rem;
  background: #ffffff;
  border-bottom: 1px solid var(--ptm-border-subtle);

  &__search {
    flex: 1 1 320px;
    max-width: 440px;
    min-width: 260px;
    position: relative;
    margin-right: auto;
  }

  &__filters {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-left: auto;
  }
}

// Search Input & Selected Chip
.ptm-search-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  background: var(--ptm-bg-subtle);
  border: 1.5px solid var(--ptm-border);
  border-radius: 12px;
  transition: all 0.18s ease;

  &--focused {
    background: #ffffff;
    border-color: var(--ptm-primary);
    box-shadow: 0 0 0 3px rgba(90, 79, 245, 0.12);
  }
}

.ptm-search-input-icon {
  position: absolute;
  left: 0.9rem;
  color: var(--ptm-text-light);
  pointer-events: none;
}

.ptm-search-field {
  width: 100% !important;
  height: 40px !important;
  padding-left: 2.45rem !important;
  padding-right: 2.2rem !important;
  border: none !important;
  background: transparent !important;
  font-size: 0.86rem !important;
  color: var(--ptm-text-dark) !important;
  box-shadow: none !important;

  &::placeholder {
    color: var(--ptm-text-light);
  }

  &:focus {
    outline: none !important;
    box-shadow: none !important;
  }
}

.ptm-search-clear-btn {
  position: absolute;
  right: 0.65rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #e2e8f0;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #fee2e2;
    color: #ef4444;
  }
}

.ptm-search-spinner {
  position: absolute;
  right: 0.75rem;
}

// Selected Student Chip
.ptm-chip-selected {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  min-height: 40px;
  padding: 0.25rem 0.5rem 0.25rem 0.7rem;
  border: 1.5px solid rgba(90, 79, 245, 0.35);
  border-radius: 12px;
  background: var(--ptm-primary-light);
  width: 100%;

  &__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 8px;
    background: linear-gradient(135deg, #5a4ff5 0%, #7c3aed 100%);
    color: #ffffff;
    font-size: 0.75rem;
    font-weight: 800;
    flex-shrink: 0;
  }

  &__info {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: 0.85rem;
    font-weight: 700;
    color: var(--ptm-text-dark);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__gr {
    font-size: 0.72rem;
    font-weight: 700;
    padding: 0.1rem 0.45rem;
    border-radius: 6px;
    background: #ffffff;
    color: var(--ptm-primary-dark);
    border: 1px solid rgba(90, 79, 245, 0.18);
    white-space: nowrap;
  }

  &__class {
    font-size: 0.74rem;
    color: var(--ptm-text-muted);
    white-space: nowrap;
  }

  &__remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: #ffffff;
    color: #ef4444;
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    transition: all 0.15s ease;

    &:hover {
      background: #fee2e2;
      transform: scale(1.08);
    }
  }
}

// Autocomplete Dropdown
.ptm-search-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 1050;
  background: #ffffff;
  border: 1px solid var(--ptm-border);
  border-radius: 14px;
  box-shadow: 0 18px 45px -10px rgba(35, 27, 85, 0.18);
  overflow: hidden;

  &__header {
    padding: 0.55rem 0.9rem;
    background: var(--ptm-bg-subtle);
    border-bottom: 1px solid var(--ptm-border-subtle);
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--ptm-text-muted);
  }
}

.ptm-search-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 1.25rem 1rem;
  font-size: 0.84rem;
  color: var(--ptm-text-muted);
  text-align: center;

  &--hint {
    color: var(--ptm-text-light);
    font-size: 0.8rem;
    padding: 0.85rem 1rem;
  }

  &--empty {
    flex-direction: column;
    padding: 1.5rem 1rem;

    .ptm-search-state__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 42px;
      height: 42px;
      border-radius: 50%;
      background: var(--ptm-primary-light);
      color: var(--ptm-primary);
      margin-bottom: 0.35rem;
    }

    strong {
      color: var(--ptm-text-dark);
      font-size: 0.88rem;
    }

    small {
      color: var(--ptm-text-light);
      font-size: 0.78rem;
    }
  }
}

.ptm-student-options {
  max-height: 250px;
  overflow-y: auto;
}

.ptm-student-option {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.65rem 0.9rem;
  border: none;
  border-bottom: 1px solid var(--ptm-border-subtle);
  background: #ffffff;
  text-align: left;
  cursor: pointer;
  transition: background 0.12s ease;

  &:hover {
    background: var(--ptm-primary-light);
  }

  &:last-child {
    border-bottom: none;
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__name {
    display: block;
    font-size: 0.86rem;
    font-weight: 700;
    color: var(--ptm-text-dark);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__meta {
    display: block;
    font-size: 0.74rem;
    color: var(--ptm-text-muted);
  }
}

// Date filter fields
.ptm-date-field-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 168px;
  max-width: 100%;
  height: 40px;
  background: #ffffff;
  border: 1.5px solid var(--ptm-border);
  border-radius: 12px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;

  &:focus-within {
    border-color: var(--ptm-primary);
    box-shadow: 0 0 0 3px rgba(90, 79, 245, 0.12);
  }

  ::v-deep {
    .flatpickr-wrapper {
      flex: 1;
      width: 100%;
      min-width: 0;
    }

    .flatpickr-input,
    input.form-control,
    .ptm-date-field {
      width: 100% !important;
      height: 38px !important;
      min-height: 38px !important;
      padding: 0 0.75rem 0 2.45rem !important;
      margin: 0 !important;
      border: none !important;
      background: transparent !important;
      box-shadow: none !important;
      outline: none !important;
      font-size: 0.86rem !important;
      font-weight: 500 !important;
      color: var(--ptm-text-dark) !important;
      cursor: pointer !important;
      line-height: 38px !important;

      &::placeholder {
        color: var(--ptm-text-light);
        font-weight: 400;
        opacity: 1;
      }

      &:focus {
        outline: none !important;
        box-shadow: none !important;
      }
    }

    input[type="hidden"] {
      position: absolute !important;
      width: 0 !important;
      height: 0 !important;
      opacity: 0 !important;
      pointer-events: none !important;
      padding: 0 !important;
      border: none !important;
    }
  }
}

.ptm-date-field-icon {
  position: absolute;
  left: 0.9rem;
  color: var(--ptm-text-light);
  pointer-events: none;
  z-index: 2;
}

// Reset Button
.ptm-btn-reset {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 40px;
  padding: 0 0.85rem;
  border: 1.5px solid #fed7aa;
  border-radius: 12px;
  background: #fff7ed;
  color: #ea580c;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #ea580c;
    color: #ffffff;
    font-size: 0.68rem;
    font-weight: 800;
  }

  &:hover {
    background: #ffedd5;
    border-color: #fdba74;
    transform: translateY(-1px);
  }
}

// 5. Table Ledger (Seamless continuation)
.ptm-table-wrap {
  position: relative;
  overflow-x: auto;
  background: #ffffff;
}

::v-deep .ptm-table {
  margin: 0;

  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #f8fafc !important;
    border: none !important;
    border-bottom: 1.5px solid #edf2f7 !important;
    padding: 0.9rem 1.25rem !important;
    font-size: 0.68rem !important;
    font-weight: 800 !important;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #475569 !important;
    white-space: nowrap;
  }

  tbody td {
    padding: 0.95rem 1.25rem !important;
    border-top: 1px solid var(--ptm-border-subtle) !important;
    vertical-align: middle !important;
    background: #ffffff;
    transition: background 0.15s ease;
  }

  tbody tr:hover td {
    background: #faf9ff !important;
  }

  tbody tr:last-child td {
    border-bottom: none !important;
  }
}

// Table Cells Styling
.ptm-cell-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  border-radius: 8px;
  background: var(--ptm-primary-light);
  color: var(--ptm-primary-dark);
  font-size: 0.76rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  border: 1px solid rgba(90, 79, 245, 0.15);
}

.ptm-student-cell {
  min-width: 150px;

  &__info {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__name {
    font-size: 0.9rem;
    font-weight: 700;
    color: var(--ptm-text-dark);
    line-height: 1.25;
  }

  &__sub {
    font-size: 0.72rem;
    color: var(--ptm-text-muted);
    margin-top: 0.15rem;
  }
}

.ptm-plain-cell {
  font-size: 0.84rem;
  font-weight: 500;
  color: var(--ptm-text-dark);
}

.ptm-date-pill-cell {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.28rem 0.75rem;
  border-radius: 999px;
  background: var(--ptm-primary-light);
  border: 1px solid rgba(90, 79, 245, 0.18);
  color: var(--ptm-primary-dark);
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;

  &__icon {
    color: var(--ptm-primary);
  }
}

.ptm-summary-card {
  position: relative;
  display: flex;
  align-items: stretch;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid #edf2f7;
  overflow: hidden;
  max-width: 480px;
  min-width: 180px;

  &__bar {
    width: 3.5px;
    background: linear-gradient(
      180deg,
      var(--ptm-primary) 0%,
      var(--ptm-accent) 100%
    );
    flex-shrink: 0;
  }

  &__text {
    margin: 0;
    padding: 0.55rem 0.85rem;
    font-size: 0.85rem;
    line-height: 1.48;
    color: #334155;
    word-break: break-word;
  }
}

// Table States (Loading & Empty)
.ptm-table-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3.5rem 1.5rem;
  text-align: center;

  &__title {
    margin-top: 0.85rem;
    font-size: 0.95rem;
    font-weight: 700;
    color: var(--ptm-text-dark);
  }

  &__desc {
    margin-top: 0.2rem;
    font-size: 0.82rem;
    color: var(--ptm-text-muted);
  }
}

.ptm-table-state--empty {
  .ptm-empty-icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 62px;
    height: 62px;
    border-radius: 50%;
    background: var(--ptm-primary-light);
    color: var(--ptm-primary);
    margin-bottom: 0.85rem;
  }

  .ptm-empty-title {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 800;
    color: var(--ptm-text-dark);
  }

  .ptm-empty-desc {
    margin: 0.35rem 0 1.25rem;
    max-width: 420px;
    font-size: 0.85rem;
    line-height: 1.45;
    color: var(--ptm-text-muted);
  }

  .ptm-empty-actions {
    display: flex;
    align-items: center;
    gap: 0.65rem;
  }
}

.ptm-btn-ghost-sm {
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.85rem;
  border: 1.5px solid var(--ptm-border);
  border-radius: 9px;
  background: #ffffff;
  color: var(--ptm-text-muted);
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: var(--ptm-bg-subtle);
    color: var(--ptm-text-dark);
  }
}

.ptm-btn-primary-sm {
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.95rem;
  border: none;
  border-radius: 9px;
  background: linear-gradient(135deg, #5a4ff5 0%, #7c3aed 100%);
  color: #ffffff;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 12px var(--ptm-primary-glow);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(90, 79, 245, 0.32);
    color: #ffffff;
  }
}

// 6. Integrated Footer Tier
.ptm-footer {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 1rem;
  padding: 0.95rem 1.85rem;
  background: #ffffff;
  border-top: 1px solid var(--ptm-border-subtle);
  border-bottom-left-radius: var(--ptm-card-radius);
  border-bottom-right-radius: var(--ptm-card-radius);

  &__left {
    display: flex;
    align-items: center;
    justify-self: start;
  }

  &__center {
    justify-self: center;
    text-align: center;
  }

  &__page-size {
    display: flex;
    align-items: center;
    gap: 0.55rem;
  }

  &__label {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--ptm-text-muted);
    white-space: nowrap;
  }

  &__range {
    font-size: 0.82rem;
    color: var(--ptm-text-muted);
    white-space: nowrap;
  }

  &__num {
    font-weight: 800;
    color: var(--ptm-text-dark);
  }

  &__right {
    display: flex;
    align-items: center;
    justify-self: end;
  }
}

// Per Page Vue-Select
::v-deep .ptm-per-page-select {
  min-width: 76px;

  .vs__dropdown-toggle {
    min-height: 34px;
    height: 34px;
    padding: 0 0.45rem;
    border: 1.5px solid var(--ptm-border) !important;
    border-radius: 9px !important;
    background: #ffffff;
    transition: all 0.15s ease;

    &:hover {
      border-color: #cbd5e1 !important;
    }
  }

  .vs__selected {
    font-size: 0.84rem;
    font-weight: 700;
    color: var(--ptm-text-dark);
    margin: 0;
  }

  .vs__actions {
    padding: 0;
  }
}

// High-End Modern Pagination
::v-deep .ptm-pagination {
  &.pagination,
  &.b-pagination {
    display: inline-flex !important;
    align-items: center !important;
    gap: 6px !important;
    margin: 0 !important;
    padding: 0 !important;
    list-style: none !important;

    // Kill all Vuexy pseudo-elements completely
    .page-link::before,
    .page-link::after,
    .prev-item .page-link::before,
    .prev-item .page-link::after,
    .next-item .page-link::before,
    .next-item .page-link::after {
      display: none !important;
      content: none !important;
      width: 0 !important;
      height: 0 !important;
      background: none !important;
      background-image: none !important;
    }

    li.page-item,
    li.page-item.prev-item,
    li.page-item.next-item,
    li.page-item ~ li.page-item,
    li.page-item ~ li.page-item:nth-child(2),
    li.page-item ~ li.page-item:nth-last-child(2),
    li.page-item:first-child,
    li.page-item:last-child {
      margin: 0 !important;
      padding: 0 !important;
      border-radius: 10px !important;
      background: transparent !important;

      a.page-link {
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        width: 36px !important;
        min-width: 36px !important;
        height: 36px !important;
        max-width: 36px !important;
        max-height: 36px !important;
        padding: 0 !important;
        margin: 0 !important;
        border-radius: 10px !important;
        border-top-left-radius: 10px !important;
        border-bottom-left-radius: 10px !important;
        border-top-right-radius: 10px !important;
        border-bottom-right-radius: 10px !important;
        border: 1.5px solid #e2e8f0 !important;
        background: #ffffff !important;
        color: #334155 !important;
        font-size: 0.85rem !important;
        font-weight: 700 !important;
        line-height: 1 !important;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
        transition: all 0.18s cubic-bezier(0.16, 1, 0.3, 1) !important;

        svg {
          display: block !important;
          stroke: #475569 !important;
          color: #475569 !important;
          width: 16px !important;
          height: 16px !important;
          transition: stroke 0.18s ease !important;
        }

        &:hover {
          background: #f5f3ff !important;
          border-color: #5a4ff5 !important;
          color: #5a4ff5 !important;
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 10px rgba(90, 79, 245, 0.18) !important;

          svg {
            stroke: #5a4ff5 !important;
            color: #5a4ff5 !important;
          }
        }
      }

      // Active state
      &.active {
        background: transparent !important;

        a.page-link {
          background: linear-gradient(
            135deg,
            #5a4ff5 0%,
            #7c3aed 100%
          ) !important;
          border-color: transparent !important;
          color: #ffffff !important;
          font-weight: 800 !important;
          box-shadow: 0 4px 14px rgba(90, 79, 245, 0.4) !important;
          transform: scale(1.05) !important;

          &:hover {
            background: linear-gradient(
              135deg,
              #5044eb 0%,
              #7033df 100%
            ) !important;
            color: #ffffff !important;
            border-color: transparent !important;
          }
        }
      }

      // Disabled state
      &.disabled {
        cursor: not-allowed !important;

        a.page-link {
          background: #f8fafc !important;
          border-color: #e2e8f0 !important;
          opacity: 0.6 !important;
          cursor: not-allowed !important;
          box-shadow: none !important;
          pointer-events: none !important;
          transform: none !important;

          svg {
            stroke: #94a3b8 !important;
            color: #94a3b8 !important;
          }
        }
      }
    }
  }
}

// Dropdown Transitions
.ptm-dropdown-fade-enter-active,
.ptm-dropdown-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.ptm-dropdown-fade-enter,
.ptm-dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

// Responsive
@media (max-width: 991px) {
  .ptm-header {
    flex-direction: column;
    align-items: flex-start;
    padding: 1.25rem;

    &__actions {
      width: 100%;

      .ptm-btn-primary {
        width: 100%;
      }
    }
  }

  .ptm-toolbar {
    flex-direction: column;
    align-items: stretch;
    padding: 1rem 1.25rem;

    &__search {
      max-width: 100%;
    }

    &__filters {
      width: 100%;
      justify-content: flex-start;

      .ptm-date-field-wrap {
        flex: 1 1 calc(50% - 0.375rem);
        min-width: 140px;
      }
    }
  }

  .ptm-footer {
    grid-template-columns: 1fr;
    justify-items: stretch;
    padding: 1rem 1.25rem;

    &__left {
      justify-self: stretch;
    }

    &__center {
      justify-self: center;
      order: 2;
    }

    &__right {
      justify-self: center;
      order: 3;
    }
  }
}
</style>

<style lang="scss">
#ptm-add-modal___BV_modal_outer_ .modal-backdrop,
body > #ptm-add-modal___BV_modal_outer_ .modal-backdrop {
  opacity: 1 !important;
  background: rgba(0, 0, 0, 0.25) !important;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.ptm-modal-wrapper {
  .modal-dialog {
    max-width: 620px;
    box-shadow: none !important;
    filter: none !important;
  }

  .ptm-modal-content {
    border: 1px solid rgba(115, 103, 240, 0.12);
    border-radius: 16px;
    box-shadow: none !important;
    max-height: 90vh;
    overflow: hidden;
  }

  .ptm-modal-header {
    background: linear-gradient(
      135deg,
      rgba(115, 103, 240, 0.1) 0%,
      rgba(255, 255, 255, 1) 100%
    );
    border-bottom: 1px solid rgba(115, 103, 240, 0.1);
    padding: 1.15rem 3.25rem 1.15rem 1.35rem;
    border-radius: 16px 16px 0 0;
    position: relative;
    overflow: visible;

    .modal-title {
      width: 100%;
      margin: 0;
      padding: 0;
      background: transparent !important;
    }

    .close {
      position: absolute;
      top: 0;
      right: 0;
      margin: 0;
      padding: 0;
      width: 46px;
      height: 46px;
      opacity: 1;
      text-shadow: none;
      float: none;
      border: none;
      border-radius: 0 16px 0 14px;
      background: #7367f0;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.35rem;
      font-weight: 400;
      line-height: 1;
      transition: transform 0.2s ease, background 0.2s ease, color 0.2s ease;

      span {
        line-height: 1;
      }

      &:hover,
      &:focus {
        opacity: 1;
        outline: none;
        background: #6254e8;
        color: #fff;
        transform: scale(1.08);
      }

      &:active {
        transform: scale(1.02);
        background: #574bd6;
      }
    }
  }

  .ptm-modal-title {
    display: flex;
    align-items: center;
    gap: 0.75rem;

    h5 {
      font-weight: 600;
      color: #4b465c;
      font-size: 1.05rem;
    }
  }

  .ptm-modal-title-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
    color: #fff;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.35);
    flex-shrink: 0;
  }

  .ptm-modal-subtitle {
    color: #a8aaae;
    font-size: 0.78rem;
  }

  .ptm-modal-body {
    padding: 1.15rem 1.35rem 1.35rem;
    background: #f8f7fc;
    overflow: visible;
  }

  .ptm-modal-form-panel {
    padding: 1rem;
    background: #fff;
    border: 1px solid rgba(115, 103, 240, 0.1);
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(115, 103, 240, 0.04);
  }

  .ptm-modal-field {
    margin-bottom: 0.95rem;

    &--last {
      margin-bottom: 0;
    }
  }

  .ptm-modal-field-label {
    display: block;
    margin-bottom: 0.45rem;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: #5e5873;
  }

  .ptm-modal-input {
    border-radius: 10px;
    border: 1.5px solid #cfcbe0;
    background: #fff;
    font-size: 0.9rem;
    font-weight: 500;
    color: #4b465c;

    &::placeholder {
      color: #8f8b99;
      font-weight: 500;
      opacity: 1;
    }

    &:focus {
      border-color: #7367f0;
      box-shadow: 0 0 0 0.15rem rgba(115, 103, 240, 0.15);
    }
  }

  .ptm-modal-search {
    position: relative;

    &--active .ptm-modal-input,
    &:focus-within .ptm-modal-input {
      border-color: rgba(115, 103, 240, 0.45);
      box-shadow: 0 0 0 0.15rem rgba(115, 103, 240, 0.12);
    }
  }

  .ptm-modal-search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #8f8b99;
    z-index: 2;
    pointer-events: none;
  }

  .ptm-modal-search-input {
    padding-left: 2.25rem;
    padding-right: 2.25rem;
    background: #fff;
    border: 1.5px solid #cfcbe0;
    height: calc(1.5em + 0.9rem + 2px);
  }

  .ptm-modal-search-clear,
  .ptm-modal-search-spinner {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
  }

  .ptm-modal-search-clear {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: #ebe9f1;
    color: #6e6b7b;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: #ffe2e3;
      color: #ea5455;
    }
  }

  .ptm-modal-search--date {
    .flatpickr-wrapper {
      width: 100%;
    }

    .flatpickr-input,
    input.form-control,
    .ptm-modal-date-input {
      width: 100% !important;
      margin: 0 !important;
      border: 1.5px solid #cfcbe0 !important;
      border-radius: 10px !important;
      background: #fff !important;
      box-shadow: none !important;
      outline: none !important;
      cursor: pointer !important;
      font-weight: 500 !important;
      color: #4b465c !important;

      &::placeholder {
        color: #8f8b99 !important;
        font-weight: 500 !important;
        opacity: 1 !important;
      }

      &:focus {
        border-color: #7367f0 !important;
        box-shadow: 0 0 0 0.15rem rgba(115, 103, 240, 0.15) !important;
      }
    }

    input[type="hidden"] {
      position: absolute !important;
      width: 0 !important;
      height: 0 !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }
  }

  .ptm-modal-selected-student {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.65rem 0.75rem;
    border: 1px solid rgba(115, 103, 240, 0.22);
    border-radius: 10px;
    background: rgba(115, 103, 240, 0.04);

    &__info {
      display: flex;
      flex-direction: column;
      gap: 0.1rem;
      min-width: 0;
    }

    &__name {
      font-size: 0.9rem;
      font-weight: 600;
      color: #4b465c;
    }

    &__gr {
      font-size: 0.76rem;
      color: #a8aaae;
    }

    &__remove {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      padding: 0;
      border: none;
      border-radius: 8px;
      background: #fff;
      color: #ea5455;
      cursor: pointer;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
      transition: all 0.15s ease;

      &:hover {
        background: #ffe2e3;
      }
    }
  }

  .ptm-modal-textarea {
    min-height: 120px;
    padding: 0.85rem 0.95rem;
    resize: vertical;
    line-height: 1.55;
    border: 1.5px solid #cfcbe0;
  }

  .ptm-modal-error {
    display: block;
    margin-top: 0.35rem;
    font-size: 0.76rem;
    font-weight: 600;
    color: #ea5455;
  }

  .ptm-modal-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.65rem;
    margin-top: 0.85rem;
  }

  .ptm-modal-cancel-btn {
    padding: 0.6rem 1.15rem;
    border: 1px solid #ebe9f1;
    border-radius: 10px;
    background: #fff;
    color: #6e6b7b;
    font-size: 0.88rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover:not(:disabled) {
      background: #f8f7fc;
      color: #4b465c;
    }

    &:disabled {
      opacity: 0.65;
      cursor: not-allowed;
    }
  }

  .ptm-modal-save-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.6rem 1.15rem;
    border: none;
    border-radius: 10px;
    background: #7367f0;
    color: #fff;
    font-size: 0.88rem;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.25);
    transition: all 0.15s ease;

    &:hover:not(:disabled) {
      background: #6254e8;
      transform: translateY(-1px);
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
      transform: none;
    }
  }
}

.ptm-flatpickr-popup.flatpickr-calendar {
  margin-top: 6px;
  border: 1px solid #e4e0ff;
  border-radius: 14px;
  box-shadow: 0 16px 40px rgba(90, 79, 245, 0.14);
  padding: 0.5rem 0.65rem 0.65rem;
  font-family: inherit;

  &::before,
  &::after {
    display: none;
  }

  .flatpickr-months {
    padding: 0.15rem 0 0.55rem;
  }

  .flatpickr-current-month {
    font-size: 0.95rem;
    font-weight: 700;
    color: #2f2b4a;

    .flatpickr-monthDropdown-months,
    input.cur-year {
      font-weight: 700;
      color: #2f2b4a;
    }
  }

  .flatpickr-weekdays {
    margin-top: 0.15rem;
  }

  .flatpickr-weekday {
    font-size: 0.72rem;
    font-weight: 700;
    color: #94a3b8;
  }

  .flatpickr-day {
    font-size: 0.84rem;
    font-weight: 600;
    color: #334155;
    border-radius: 8px;
    max-width: 36px;
    height: 36px;
    line-height: 36px;

    &:hover {
      background: #f3f1ff;
      border-color: #f3f1ff;
    }

    &.selected,
    &.selected:hover {
      background: linear-gradient(135deg, #5a4ff5, #7c3aed);
      border-color: transparent;
      color: #ffffff;
      font-weight: 700;
    }

    &.today {
      border-color: #5a4ff5;
      color: #5a4ff5;
      font-weight: 700;

      &.selected {
        color: #ffffff;
      }
    }

    &.prevMonthDay,
    &.nextMonthDay {
      color: #cbd5e1;
      font-weight: 500;
    }
  }

  .flatpickr-months .flatpickr-prev-month,
  .flatpickr-months .flatpickr-next-month {
    fill: #5a4ff5;

    &:hover svg {
      fill: #7c3aed;
    }
  }
}

/* ─── PRINT VIEW OVERLAY ────────────────────────────────────────────── */
.ptm-print-view {
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: #f8fafc;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.ptm-print-controls {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 2rem;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  &__right {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
}

.ptm-print-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.5rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  color: #475569;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
  }
}

.ptm-print-count-label {
  font-size: 0.82rem;
  color: #64748b;
  font-weight: 500;
}

.ptm-print-now-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.55rem 1.25rem;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #5a4ff5 0%, #7c3aed 100%);
  color: #ffffff;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(90, 79, 245, 0.25);
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    box-shadow: 0 6px 18px rgba(90, 79, 245, 0.35);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.ptm-print-loading,
.ptm-print-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 1rem;
  padding: 3rem;
  color: #475569;

  &__text {
    font-size: 0.95rem;
    font-weight: 600;
    color: #64748b;
  }
}

.ptm-print-document {
  max-width: 1000px;
  width: 100%;
  margin: 2rem auto;
  background: #ffffff;
  padding: 2.5rem 3rem;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.ptm-print-doc-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  border-bottom: 2px solid #0f172a;
  padding-bottom: 1.25rem;
  margin-bottom: 1.5rem;

  &__left {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.25rem;
    font-size: 0.82rem;
    color: #64748b;
  }
}

.ptm-print-doc-title {
  font-size: 1.5rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.02em;
}

.ptm-print-doc-subtitle {
  font-size: 0.88rem;
  color: #475569;
  margin: 0;
  display: flex;
  align-items: center;
}

.ptm-print-doc-date {
  font-weight: 500;
}

.ptm-print-doc-total {
  font-size: 0.85rem;
  color: #334155;
}

.ptm-print-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 0.5rem;

  th {
    background: #f8fafc;
    color: #1e293b;
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.65rem 0.85rem;
    border-top: 1px solid #e2e8f0;
    border-bottom: 2px solid #cbd5e1;
    text-align: left;
  }

  td {
    padding: 0.65rem 0.85rem;
    border-bottom: 1px solid #e2e8f0;
    font-size: 0.84rem;
    color: #334155;
    vertical-align: top;
  }

  .ptm-print-th--idx,
  .ptm-print-td--idx {
    width: 45px;
    text-align: center;
    color: #64748b;
    font-weight: 600;
  }

  .ptm-print-th--summary,
  .ptm-print-td--summary {
    max-width: 320px;
    word-break: break-word;
    line-height: 1.45;
  }

  .ptm-print-td--class {
    white-space: nowrap;
    font-weight: 600;
    color: #1e293b;
  }

  .ptm-print-td--date {
    white-space: nowrap;
    color: #64748b;
  }
}

.ptm-print-student {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;

  &__name {
    font-weight: 700;
    color: #0f172a;
  }

  &__gr {
    font-size: 0.74rem;
    color: #64748b;
    font-weight: 600;
  }
}

.ptm-print-empty {
  text-align: center;
  padding: 2.5rem 1rem !important;
  color: #94a3b8;
  font-style: italic;
}

.ptm-print-doc-footer {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
  font-size: 0.8rem;
  color: #64748b;
  text-align: right;
  font-weight: 600;
}
</style>

<!-- ─── PTM PRINT MEDIA STYLES ─────────────────────────────────────────── -->
<style>
@media print {
  @page {
    margin: 10mm 12mm;
    size: auto;
  }

  .no-print,
  .ptm-print-controls,
  nav,
  aside,
  header.navbar,
  footer,
  .main-menu,
  .navbar,
  [class*="sidebar"] {
    display: none !important;
  }

  body,
  .manage-ptm {
    background: #fff !important;
    padding: 0 !important;
  }

  .ptm-card {
    display: none !important;
  }

  .ptm-print-view {
    position: static !important;
    background: transparent !important;
    overflow: visible !important;
    inset: auto !important;
    display: block !important;
    padding: 0 !important;
  }

  .ptm-print-document {
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    box-shadow: none !important;
    border-radius: 0 !important;
  }

  .ptm-print-doc-header {
    border-bottom: 1.5px solid #000 !important;
    padding-bottom: 8px !important;
    margin-bottom: 14px !important;
  }

  .ptm-print-table {
    border: 1px solid #cbd5e1 !important;
    page-break-inside: auto;
  }

  .ptm-print-table th {
    background: #f1f5f9 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    font-size: 10px !important;
    padding: 6px 8px !important;
    border-bottom: 1.5px solid #000 !important;
  }

  .ptm-print-table td {
    font-size: 10px !important;
    padding: 5px 8px !important;
    border-bottom: 1px solid #e2e8f0 !important;
  }

  .ptm-print-tr {
    page-break-inside: avoid;
    page-break-after: auto;
  }

  .ptm-print-doc-footer {
    border-top: 1px solid #000 !important;
    padding-top: 6px !important;
    margin-top: 10px !important;
    font-size: 10px !important;
  }
}
</style>
